"use client";

import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import Link from "next/link";
import { ArrowRightIcon } from "lucide-react";
import Skeleton from "react-loading-skeleton";
import EventCard from "./event-card";
import { useCountryContext } from "@/providers/country-provider";
import { EVENTS_API } from "@/lib/apis";
import { TEvent } from "@/types/events";

const UpcomingEvents = () => {
	const [allEvents, setAllEvents] = useState<TEvent[]>([]);
	const [loading, setLoading] = useState(true);

	const pathname = usePathname();
	const { name, country, portals } = useCountryContext();

	const isCountryPortalPage = pathname === portals?.current?.href;

	useEffect(() => {
		const fetchEvents = async () => {
			try {
				const response = await fetch(`${EVENTS_API}`, {
					headers: {
						"Content-Type": "application/json",
					},
				});
				const data = await response.json();

				if (!response.ok) throw new Error(data.error || "Failed to fetch events");

				const now = new Date();
				const upcomingEvents = (data?.data?.events || [])
					.filter((event: { dateTime: string }) => new Date(event.dateTime) > now)
					.sort(
						(a: any, b: any) => new Date(a.dateTime).getTime() - new Date(b.dateTime).getTime()
					);

				setAllEvents(upcomingEvents);
			} catch (error) {
				console.error("Error fetching upcoming events:", error);
			} finally {
				setLoading(false);
			}
		};

		fetchEvents();
	}, []);

	const filteredEvents = isCountryPortalPage
		? allEvents
				.filter((event) => {
					const countryValue = event.rawEventData.address?.country;
					return countryValue && country.includes(countryValue);
				})
				.slice(0, 3)
		: allEvents.slice(0, 3);

	return (
		<section>
			<div className="mb-7 flex items-center justify-between gap-x-4">
				<h2 className="text-xl font-bold text-gray-900 sm:whitespace-nowrap md:text-2xl">
					{isCountryPortalPage ? name : "Global"} Real Estate Events
				</h2>

				<div className="hidden h-[0.5px] w-full bg-gray-200 sm:block" />

				<Link
					href="/events"
					className="flex w-fit items-center justify-start gap-x-2 text-sm font-medium text-brand-primary"
				>
					<span className="whitespace-nowrap">See more events</span>
					<ArrowRightIcon className="h-4 w-4" />
				</Link>
			</div>

			<div className="mx-auto grid max-w-2xl grid-cols-1 gap-6 lg:mx-0 lg:max-w-none lg:grid-cols-3">
				{loading
					? Array.from({ length: 3 }).map((_, i) => (
							<Skeleton key={i} height={256} className="rounded-md" />
					  ))
					: filteredEvents.map((event) => (
							<EventCard key={event.id} event={event} className="bg-white" />
					  ))}
			</div>
		</section>
	);
};

export default UpcomingEvents;
